// src/components/LoggedInHeader.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import '../assets/styles/LoggedInHeader.css';
import logo from '../assets/images/favicon2.png';
import reportBug from '../assets/images/Bug.png';
import user from '../assets/images/User.png';
import referFriend from '../assets/images/referFriend.png';
import ReportBugPopup from './ReportBugPopup';
import AccountDropdown from './AccountDropdown';
import MobileNavDrawer from './MobileNavDrawer';

/**
 * LoggedInHeader Component
 *
 * Enhanced header component with support for unsaved changes confirmation
 * Can be used across the application including in the Dashboard
 */
const LoggedInHeader = ({
  isEditMode = false,
  hasUnsavedChanges = false,
  onNavigationRequest,
  setPendingNavigation,
  setShowLeaveConfirmation,
  onAddChartsClick
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Navigation items with their paths
  const navItems = [
    { name: 'My Dashboard', path: '/my-dashboard' },
    { name: 'Add Charts', path: '/add-charts' },
    { name: 'Live Charts', path: '/live-charts' },
    { name: 'Blog', path: '/blogged-in' },
    { name: 'Learn', path: '/learn' },
    { name: 'Submit Idea', path: '/submit-idea' }
  ];

  // Handle navigation with unsaved changes check
  const handleNavigation = (path) => {
    if (onNavigationRequest) {
      // If parent provides a navigation handler, use it
      onNavigationRequest(path);
    } else {
      // Direct navigation if no handler provided
      navigate(path);
    }
  };

  // Handle a nav item click. "Add Charts" is special-cased: when we're
  // already on the Dashboard (which passes onAddChartsClick), open the
  // drawer in place instead of navigating - /add-charts as a route only
  // exists now as a redirect for old links/bookmarks (see routes.js).
  const handleNavItemClick = (item) => {
    if (item.path === '/add-charts' && onAddChartsClick) {
      onAddChartsClick();
      return;
    }
    handleNavigation(item.path);
  };

  const handleReferClick = () => {
    if (isEditMode && hasUnsavedChanges && setShowLeaveConfirmation) {
      // Show confirmation before navigating
      if (setPendingNavigation) {
        setPendingNavigation('/refer-a-friend');
      }
      setShowLeaveConfirmation(true);
    } else {
      // Navigate directly to refer a friend page
      handleNavigation('/refer-a-friend');
    }
  };

  const handleBugClick = () => {
    if (isEditMode && hasUnsavedChanges && setShowLeaveConfirmation) {
      // Show confirmation before opening popup
      if (setPendingNavigation) {
        setPendingNavigation(null); // Special case, we'll handle opening the popup after confirmation
      }
      setShowLeaveConfirmation(true);
    } else {
      setIsPopupOpen(true);
    }
  };

  // Mobile drawer items reuse the exact same click handlers as the desktop
  // nav/icons, so the unsaved-changes funnel and the Add Charts drawer
  // special case behave identically from the hamburger menu.
  const mobileNavItems = [
    ...navItems.map((item) => ({
      label: item.name,
      onClick: () => handleNavItemClick(item),
      selected: location.pathname === item.path
    })),
    { label: 'Refer a Friend', onClick: handleReferClick, selected: location.pathname === '/refer-a-friend' },
    { label: 'Report Bug', onClick: handleBugClick }
  ];

  return (
    <div className="header-container">
      <div className="header-content">
        <button
          className="ft-hamburger-btn ft-hamburger-btn--logged-in"
          onClick={() => setIsMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <Menu size={24} />
        </button>
        {/* Logo Section */}
        <div
          className="logo-section"
          onClick={() => handleNavigation('/')}
          style={{ cursor: 'pointer' }}
        >
          <img src={logo} alt="FINNTECH" className="logged-in-logo" />
        </div>

        {/* Navigation Links */}
        <nav className="nav-links">
          {navItems.map((item) => (
            <div
              key={item.path}
              className={`nav-item ${location.pathname === item.path ? 'selected' : ''}`}
              onClick={() => handleNavItemClick(item)}
            >
              {item.name}
            </div>
          ))}
        </nav>

        {/* Right Icons */}
        <div className="right-icons">
          {/* Refer a Friend Icon */}
          <div className="icon-container ft-header-icon-refer">
            <img
              src={referFriend}
              alt="Refer a Friend"
              className="header-icon"
              onClick={handleReferClick}
            />
          </div>

          {/* Report Bug Icon */}
          <div className="icon-container ft-header-icon-bug">
            <img
              src={reportBug}
              alt="Report Bug"
              className="header-icon"
              onClick={handleBugClick}
            />
          </div>

          {/* User Profile Icon */}
          <div className="icon-container">
            <img
              src={user}
              alt="User Profile"
              className="header-icon"
              onClick={() => {
                if (isEditMode && hasUnsavedChanges && setShowLeaveConfirmation) {
                  // Show confirmation before opening dropdown
                  if (setPendingNavigation) {
                    setPendingNavigation(null); // Special case for dropdown
                  }
                  setShowLeaveConfirmation(true);
                } else {
                  setIsDropdownOpen(!isDropdownOpen);
                }
              }}
            />
            {/* Only show dropdown if not in edit mode */}
            {isDropdownOpen && (!isEditMode || !hasUnsavedChanges) && (
              <AccountDropdown handleNavigation={handleNavigation} />
            )}
          </div>
        </div>
      </div>

      {/* Bug report popup - only shown when not in edit mode with unsaved changes */}
      {isPopupOpen && (!isEditMode || !hasUnsavedChanges) && (
        <ReportBugPopup onClose={() => setIsPopupOpen(false)} />
      )}

      {/* Mobile slide-out navigation */}
      <MobileNavDrawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        items={mobileNavItems}
        variant="loggedIn"
      />
    </div>
  );
};

export default LoggedInHeader;
